import { FC, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import { EmptyVersionGuide } from "../EmptyVersionGuide";
import { UploadButton } from "../UploadButton";
import { DocumentsDropzone } from "./DocumentsDropzone";

import { HelpCircle, Mic } from "lucide-react";
import { useWebsocket } from "~/hooks/useWebsocket";
import {
  DocumentUpdateLog,
  FileWithHash,
  useDocumentsList,
} from "../../hooks/useDocumentsList";
import { VoiceRecorderModal } from "../VoiceRecorderModal";

import clsx from "clsx";
import { useListProjects } from "~/api/folders/folders";
import { useGetOrganization } from "~/api/organizations/organizations";
import { useGetProject } from "~/api/projects/projects";
import { CaseStatusBadge } from "~/components/CaseStatusBadge";
import { PaymentRequiredBanner } from "~/features/billing/PaymentRequiredBanner";
import { ROUTES, useProjectParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useToast } from "~/hooks/use-toast";
import {
  useProjectIndexStatus,
  useReindexProject,
} from "~/lib/knowledge/reindex";
import { trackEvent } from "~/utils/analytics";
import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import { useParentCase } from "../../hooks/useParentCase";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { CaseActions } from "../CaseActions";
import { CaseHeaderProperties } from "../CaseHeaderProperties";
import { EditVersionNameButton } from "../EditVersionNameButton";
import { NewChatButton } from "../NewChatButton";
import { DocumentsGroupedList } from "./DocumentsGroupedList";
import { DocumentsListHeader } from "./DocumentsListHeader";
import { DocumentsPanel } from "./DocumentsPanel";
import { DuplicateFilesModal } from "./DuplicateFilesModal";
import { PipedriveFormModal } from "./PipedriveFormModal";
import { UploadsBanner } from "./UploadsBanner";
import { VersionPanel } from "./VersionPanel";

export const DocumentsList: FC = () => {
  const { organizationId, caseId } = useProjectParams();
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isVoiceRecorderOpen, setIsVoiceRecorderOpen] = useState(false);
  const { isMobile, isLoading: isMobileLoading } = useMobile();
  const [showPipedriveModal, setShowPipedriveModal] = useState(false);
  const [firstUploadPending, setFirstUploadPending] = useState(false);
  const { project, isNested, isMaster } = useParentCase();
  const currentCase = useGetProject(organizationId, caseId).data;
  const { isSelectionMode } = useDocumentSelectionStore();
  const intl = useIntl();
  const { toast } = useToast();
  const { mutateAsync: reindexProject, isPending: isReindexing } =
    useReindexProject();
  const { data: indexStatus, refetch: refetchIndexStatus } =
    useProjectIndexStatus(organizationId, caseId, {
      refetchOnIndexing: true,
    });
  const {
    documents,
    isLoading,
    isError,
    isEmpty,
    hasOnlyArchivedDocuments,
    bulkUpdateDocuments,
    handleUpload,
    duplicateFiles,
    pendingFiles,
    showDuplicatesModal,
    handleDuplicateConfirm,
    handleDuplicateCancel,
    handleContinueWithoutDuplicates,
    counts,
    totalPages,
    allFilesNotProcessed,
    uploadProgress,
    cancelDraftUpload,
  } = useDocumentsList(organizationId, caseId);

  // Get document selection from Zustand store for cleanup only
  const { clearSelection } = useDocumentSelectionStore();

  // Reset selection when documents change or component unmounts
  useEffect(() => {
    clearSelection();
  }, [caseId, organizationId, clearSelection]);

  const handleReindex = async () => {
    try {
      const result = await reindexProject({
        organizationId,
        projectId: caseId,
      });
      // Refetch status to show progress
      refetchIndexStatus();
      toast({
        title: intl.formatMessage({
          id: "case.reindexStarted",
          defaultMessage: "Reindex started",
        }),
        description: intl.formatMessage(
          {
            id: "case.reindexStartedDesc",
            defaultMessage:
              "Processing {count} pages. This may take a few minutes.",
          },
          { count: result.total_documents }
        ),
      });
    } catch (error) {
      console.error("Error reindexing case:", error);
      toast({
        title: intl.formatMessage({
          id: "case.reindexError",
          defaultMessage: "Reindex failed",
        }),
        description: intl.formatMessage({
          id: "case.reindexErrorDesc",
          defaultMessage: "Failed to start reindex. Please try again.",
        }),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const visitTime = Date.now();
    const initialDocumentCount = documents.length;

    trackEvent({
      eventName: "app/case/documents_page.page/visit",
      properties: {
        caseId,
        organizationId,
        documentCount: initialDocumentCount,
        timestamp: new Date().toISOString(),
      },
    });

    return () => {
      const timeSpentSeconds = Math.floor((Date.now() - visitTime) / 1000);
      const hadActiveUploadsAtLeave = hasActiveUploads;
      trackEvent({
        eventName: "app/case/documents_page.page/leave",
        properties: {
          caseId,
          timeSpentSeconds,
          hadActiveUploads: hadActiveUploadsAtLeave,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, organizationId]);

  const { addListener, removeListener } = useWebsocket<DocumentUpdateLog>({
    projectId: caseId,
    subscribeTo: "document-update-log",
  });

  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );

  // TODO: SKY-1441 - REVERT BEFORE PRODUCTION RELEASE - restore US timezone check
  const isUSCustomer = true;

  const isPaymentRequired =
    organizationInfo?.serviceStatus === "PAYMENT_REQUIRED";

  // Fetch a minimal page of cases to get the total count
  const { data: activeCasesSummary } = useListProjects(organizationId, {
    archived: false,
    page: 1,
    limit: 1,
  });
  const { data: archivedCasesSummary } = useListProjects(organizationId, {
    archived: true,
    page: 1,
    limit: 1,
  });

  // If this is the organization's first active case, allow showing the modal once on first upload
  useEffect(() => {
    if (activeCasesSummary && archivedCasesSummary) {
      const totalItems =
        (activeCasesSummary.pagination?.totalItems || 0) +
        (archivedCasesSummary.pagination?.totalItems || 0);
      if (totalItems === 1) {
        setFirstUploadPending(true);
      }
    }
  }, [activeCasesSummary, archivedCasesSummary]);

  const handlePipedriveModal = () => {
    if (!firstUploadPending) return;
    setShowPipedriveModal(true);
    setFirstUploadPending(false);
  };

  const handleFilesSelected = async (files: File[]) => {
    await handleUpload(files as FileWithHash[]);
    handlePipedriveModal();
    setIsUploadDialogOpen(false);
    // setTimeout(()=>{
    //   setIsUploadDialogOpen(false)
    // }, 1000)
  };

  useEffect(() => {
    addListener(bulkUpdateDocuments);
    return () => removeListener(bulkUpdateDocuments);
  }, [addListener, removeListener, bulkUpdateDocuments]);

  const { getRootProps, isDragActive: dropzoneIsDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: isNested || isPaymentRequired,
    onDrop: () => {
      // Do nothing when in nested version
      if (isNested) return;
    },
  });

  const hasActiveUploads =
    Object.values(uploadProgress).reduce((sum, cur) => {
      return sum + (cur == 100 ? 0 : 1);
    }, 0) > 0;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveUploads]);

  if (isLoading || isMobileLoading)
    return <Loader className="h-[calc(100vh-140px)]" />;

  if (isError)
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-100px)]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-spin"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <div className="text-center mt-4">
          <FormattedMessage
            id="documentsList.failedFetchingDocuments"
            defaultMessage={
              "Couldn't fetch the list of documents. Please refresh this page"
            }
          />
        </div>
      </div>
    );

  const nonDocListHeight = 65 + 60 + 104;

  const topPx =
    nonDocListHeight - window.scrollY > 0
      ? nonDocListHeight - window.scrollY
      : 0;
  const dropzoneHeight = `calc(100vh - ${topPx}px)`;

  return (
    <>
      <UploadsBanner visible={hasActiveUploads} />
      <div
        {...getRootProps()}
        className="px-4 md:px-6 relative min-h-[calc(100vh-89px)] flex flex-col overflow-x-hidden"
      >
        {isPaymentRequired && (
          <div className="py-6">
            <PaymentRequiredBanner />
          </div>
        )}
        <div className="flex justify-between items-center pt-6">
          <div className="flex gap-2 items-center">
            <Breadcrumbs
              path={[
                {
                  name: intl.formatMessage({
                    id: "cases",
                    defaultMessage: "Cases",
                  }),
                  href: ROUTES.CASES({ organizationId }),
                },
                ...(isNested && project
                  ? [
                      {
                        name: project.name,
                        href: ROUTES.CASE({
                          organizationId,
                          caseId: project.id,
                        }),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-start items-center gap-2">
            <h1 className="text-2xl font-bold truncate">{currentCase?.name}</h1>
            <CaseStatusBadge
              counts={counts}
              chatStatus={currentCase?.chatStatus}
            />
            {currentCase?.id && isMaster && (
              <CaseActions
                caseId={currentCase.id}
                indexStatus={indexStatus}
                isReindexing={isReindexing}
                onReindex={handleReindex}
                fileCount={counts.fileCount}
              />
            )}
            {currentCase?.id && !isMaster && <EditVersionNameButton />}
          </div>
          {currentCase && (
            <CaseHeaderProperties
              totalPages={totalPages}
              createdAt={currentCase.createdAt}
              author={currentCase.author}
            />
          )}
        </div>
        <DuplicateFilesModal
          isOpen={showDuplicatesModal}
          duplicateFiles={duplicateFiles.map((d) => d.name)}
          pendingFiles={pendingFiles}
          onConfirm={handleDuplicateConfirm}
          onContinueWithoutDuplicates={handleContinueWithoutDuplicates}
          onCancel={handleDuplicateCancel}
        />
        <div
          className={clsx("relative flex-1 flex flex-col", isMobile && "pb-20")}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
            <VersionPanel
              disableClone={
                counts.fileCount === 0 ||
                counts.fileCount !== counts.complete_count
              }
              hideActions={isEmpty && isMaster}
            />
            {!isMobile && (
              <div className="flex gap-2 items-center flex-wrap">
                {isUSCustomer && isMaster && (
                  <Button
                    onClick={() => {
                      trackEvent({
                        eventName: "app/case/record_session.button/click",
                      });
                      setIsVoiceRecorderOpen(true);
                    }}
                    variant="ghost"
                    className="text-blue-600 hover:text-blue-700"
                    disabled={!!(isPaymentRequired || isNested)}
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    <FormattedMessage
                      id="documentsList.recordSession"
                      defaultMessage="Record"
                    />
                  </Button>
                )}
                {!isEmpty && isMaster && (
                  <UploadButton
                    isModalOpen={isUploadDialogOpen}
                    setIsModalOpen={setIsUploadDialogOpen}
                    onFilesSelected={handleFilesSelected}
                    disabled={!!(isPaymentRequired || isNested)}
                    caseId={caseId}
                    organizationId={organizationId}
                    autoCloseModal={false}
                  />
                )}
                <NewChatButton
                  documentIds={documents.map((doc) => doc.id)}
                  disabled={allFilesNotProcessed}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>
          {isEmpty && !hasOnlyArchivedDocuments && isMaster && (
            <DocumentsDropzone
              onDrop={handleFilesSelected}
              className="flex-1 flex flex-col"
              disabled={!!(isPaymentRequired || isNested)}
            />
          )}
          {hasOnlyArchivedDocuments && <EmptyVersionGuide />}
          {!isEmpty && (
            <>
              <DocumentsListHeader
                documents={documents}
                allFilesNotProcessed={allFilesNotProcessed}
              />
              <DocumentsGroupedList
                allFilesNotProcessed={allFilesNotProcessed}
                documents={documents}
                uploadProgress={uploadProgress}
                onCancelDraftUpload={cancelDraftUpload}
                key={caseId}
              />
            </>
          )}
        </div>

        {!isEmpty &&
          !isUploadDialogOpen &&
          dropzoneIsDragActive &&
          isMaster && (
            <div
              className={`fixed inset-0 bg-background/90 z-10 flex items-center justify-center p-6`}
              style={{
                height: dropzoneHeight,
                top: topPx ? topPx : 0,
              }}
            >
              <DocumentsDropzone
                onDrop={handleFilesSelected}
                className="h-full w-full"
                disabled={isPaymentRequired}
              />
            </div>
          )}

        {isSelectionMode() && <DocumentsPanel />}

        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border px-2 py-3 flex items-center justify-around z-20 pb-safe-bottom">
            {isUSCustomer && isMaster && (
              <Button
                onClick={() => {
                  trackEvent({
                    eventName: "app/case/record_session.button/click",
                  });
                  setIsVoiceRecorderOpen(true);
                }}
                variant="ghost"
                className="text-blue-600 hover:text-blue-700"
                disabled={!!(isPaymentRequired || isNested)}
              >
                <Mic className="w-4 h-4 mr-1" />
                <FormattedMessage
                  id="documentsList.recordSession"
                  defaultMessage="Record"
                />
              </Button>
            )}
            {!isEmpty && isMaster && (
              <UploadButton
                isModalOpen={isUploadDialogOpen}
                setIsModalOpen={setIsUploadDialogOpen}
                onFilesSelected={handleFilesSelected}
                disabled={!!(isPaymentRequired || isNested)}
                caseId={caseId}
                organizationId={organizationId}
                autoCloseModal={false}
              />
            )}
            <NewChatButton
              documentIds={documents.map((doc) => doc.id)}
              disabled={allFilesNotProcessed}
              totalPages={totalPages}
              mobileLabel={intl.formatMessage({
                id: "documentsList.explore",
                defaultMessage: "Explore",
              })}
              useMobileStyle
            />
            <Button
              onClick={() => {
                if (window.Beacon) {
                  window.Beacon("open");
                }
              }}
              variant="ghost"
              className="text-blue-600 hover:text-blue-700"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              <FormattedMessage id="mobile.nav.help" defaultMessage="Help" />
            </Button>
          </div>
        )}
      </div>
      <PipedriveFormModal
        isOpen={showPipedriveModal}
        onClose={() => setShowPipedriveModal(false)}
        onSubmitted={() => {
          setShowPipedriveModal(false);
        }}
      />
      <VoiceRecorderModal
        open={isVoiceRecorderOpen}
        onOpenChange={setIsVoiceRecorderOpen}
        organizationId={organizationId}
        caseId={caseId}
      />
    </>
  );
};
